const cards = [
  {
    id: 'share',
    icon: 'ri-share-line',
    title: 'Share CSCF Support',
    tagline: 'Spread the Word',
    description:
      'The single most powerful thing you can do is share this website with anyone who might benefit — families, doctors, teachers, or friends. The more people who know about CSCF, the faster families find answers.',
    actions: [
      { label: 'Share on Facebook', icon: 'ri-facebook-circle-fill', href: 'https://www.facebook.com/sharer/sharer.php?u=https://cscfsupport.org', style: 'bg-blue-500 hover:bg-blue-600 text-white' },
      { label: 'Share on X', icon: 'ri-twitter-x-line', href: 'https://twitter.com/intent/tweet?text=CSCF+Support+%E2%80%94+Connecting+families+affected+by+Cardiospondylocarpofacial+syndrome.+%23RareDisease&url=https://cscfsupport.org', style: 'bg-gray-900 hover:bg-gray-800 text-white' },
    ],
    bg: 'bg-brand-50',
    border: 'border-brand-100',
    iconBg: 'bg-brand-100',
    iconColor: 'text-brand-600',
    accentBar: 'bg-brand-400',
    steps: [
      'Post about CSCF on your social media',
      'Share this site with your child\'s medical team',
      'Tell other rare disease families about this community',
    ],
  },
  {
    id: 'community',
    icon: 'ri-group-2-line',
    title: 'Join the Community',
    tagline: 'Connect with Families',
    description:
      'Whether you are a parent, patient, caregiver, or supporter — there is a place for you in the CSCF community. Join our private Facebook group, sign up for updates, or share your story.',
    actions: [
      { label: 'Join the Community', icon: 'ri-group-line', href: 'https://www.facebook.com/share/g/1BT8NGBZVX/?mibextid=K35XfP', style: 'bg-brand-500 hover:bg-brand-600 text-white' },
      { label: 'Share Your Story', icon: 'ri-quill-pen-line', href: '/community#story-form', style: 'bg-white hover:bg-gray-50 text-brand-600 border-2 border-brand-200' },
    ],
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accentBar: 'bg-emerald-400',
    steps: [
      'Join our private Facebook group for families',
      'Sign up for research and community updates',
      'Share your diagnosis journey to help others',
    ],
  },
  {
    id: 'awareness',
    icon: 'ri-megaphone-line',
    title: 'Participate in Awareness',
    tagline: 'Raise Your Voice',
    description:
      'Rare Disease Day is February 28th each year. But awareness for CSCF can happen any day. Wearing a ribbon, posting on social media, or speaking to your local school or hospital can all make a real impact.',
    actions: [
      { label: 'Rare Disease Day', icon: 'ri-calendar-event-line', href: 'https://www.rarediseaseday.org/', style: 'bg-accent-500 hover:bg-accent-600 text-white' },
      { label: 'NORD Resources', icon: 'ri-global-line', href: 'https://rarediseases.org/', style: 'bg-white hover:bg-gray-50 text-accent-600 border-2 border-accent-200' },
    ],
    bg: 'bg-accent-50',
    border: 'border-accent-100',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    accentBar: 'bg-accent-400',
    steps: [
      'Participate in Rare Disease Day (Feb 28)',
      'Share CSCF facts on social media',
      'Talk to your child\'s school about rare disease awareness',
    ],
  },
  {
    id: 'research',
    icon: 'ri-microscope-line',
    title: 'Support Research',
    tagline: 'Advance the Science',
    description:
      'Research is the path to better treatments and, ultimately, a cure. You can support research by connecting with researchers, participating in studies, or helping us build a global patient registry.',
    actions: [
      { label: 'Contact Researchers', icon: 'ri-mail-send-line', href: '/research#contact-researchers', style: 'bg-red-500 hover:bg-red-600 text-white' },
      { label: 'View Published Studies', icon: 'ri-file-paper-2-line', href: '/science', style: 'bg-white hover:bg-gray-50 text-red-500 border-2 border-red-200' },
    ],
    bg: 'bg-red-50',
    border: 'border-red-100',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    accentBar: 'bg-red-400',
    steps: [
      'Connect us with researchers or clinicians you know',
      'Participate in natural history studies when available',
      'Help build the global CSCF patient registry',
    ],
  },
];

export default function ActionCards() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* Section header */}
        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading mb-3">
            Four Ways to Make a Difference
          </h2>
          <p className="text-gray-500 text-base font-body max-w-xl mx-auto leading-relaxed">
            Every action — big or small — helps build a stronger community and a brighter future for CSCF families.
          </p>
        </div>

        {/* 2x2 card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`rounded-2xl border ${card.bg} ${card.border} overflow-hidden flex flex-col`}
            >
              {/* Accent top bar */}
              <div className={`h-1.5 w-full ${card.accentBar}`} />

              <div className="p-8 flex flex-col flex-grow">
                {/* Tag */}
                <span className={`text-xs font-semibold uppercase tracking-widest font-heading mb-4 ${card.iconColor}`}>
                  {card.tagline}
                </span>

                {/* Icon + Title row */}
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-14 h-14 flex items-center justify-center ${card.iconBg} rounded-2xl flex-shrink-0`}>
                    <i className={`${card.icon} text-2xl ${card.iconColor}`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-heading">{card.title}</h3>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed font-body mb-6">{card.description}</p>

                {/* Steps */}
                <ul className="space-y-2 mb-7 flex-grow">
                  {card.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-500 font-body">
                      <div className={`w-5 h-5 flex items-center justify-center ${card.iconBg} rounded-full flex-shrink-0 mt-0.5`}>
                        <i className={`ri-check-line text-xs ${card.iconColor}`}></i>
                      </div>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {card.actions.map((action) => (
                    <a
                      key={action.label}
                      href={action.href}
                      target={action.href.startsWith('http') ? '_blank' : undefined}
                      rel={action.href.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer font-body ${action.style}`}
                    >
                      <i className={action.icon}></i>
                      {action.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Impact stats strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '< 30', label: 'Confirmed cases worldwide', icon: 'ri-search-eye-line', color: 'text-brand-500' },
            { value: '6+', label: 'Countries with known cases', icon: 'ri-global-line', color: 'text-emerald-500' },
            { value: '7+', label: 'Published case studies', icon: 'ri-file-paper-2-line', color: 'text-accent-500' },
            { value: '2016', label: 'Year gene was identified', icon: 'ri-test-tube-line', color: 'text-red-400' },
          ].map((stat) => (
            <div key={stat.label} className="bg-gray-50 border border-gray-100 rounded-2xl p-5 text-center">
              <div className="w-9 h-9 flex items-center justify-center bg-white border border-gray-100 rounded-xl mx-auto mb-3">
                <i className={`${stat.icon} ${stat.color} text-base`}></i>
              </div>
              <div className={`text-2xl font-bold font-heading mb-1 ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-400 font-body leading-snug">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
